import { Box, Card, CircularProgress, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import dayjs from 'dayjs';
import { RT } from '../../../retention/theme/retentionTheme';

// Look & feel shared by the three attendance views: gradient KPI cards, rings, bars and the attendance bands.

export const DISPLAY_FONT = '"Public Sans", sans-serif';
export const BODY_FONT = '"Inter", "Public Sans", sans-serif';

export const GRADIENTS = {
  indigo: 'linear-gradient(135deg,#4338CA 0%,#6366F1 55%,#818CF8 100%)',
  violet: 'linear-gradient(135deg,#6D28D9 0%,#8B5CF6 55%,#C084FC 100%)',
  emerald: 'linear-gradient(135deg,#047857 0%,#10B981 60%,#6EE7B7 100%)',
  amber: 'linear-gradient(135deg,#B45309 0%,#F59E0B 60%,#FCD34D 100%)',
  rose: 'linear-gradient(135deg,#BE123C 0%,#F43F5E 60%,#FDA4AF 100%)',
  sky: 'linear-gradient(135deg,#0369A1 0%,#0EA5E9 60%,#7DD3FC 100%)',
  teal: 'linear-gradient(135deg,#0F766E 0%,#14B8A6 60%,#5EEAD4 100%)',
  slate: 'linear-gradient(135deg,#1E293B 0%,#334155 60%,#64748B 100%)',
};

export const HERO = 'linear-gradient(120deg,#1E1B4B 0%,#3730A3 40%,#6D28D9 75%,#A21CAF 100%)';

// Attendance bands, best first. Low attendance starts below 75%.
export const BANDS = [
  { key: 'excellent', label: 'Excellent', range: '90% +', color: '#16A34A' },
  { key: 'good', label: 'Good', range: '75 – 89%', color: '#0EA5E9' },
  { key: 'low', label: 'Low', range: '50 – 74%', color: '#F59E0B' },
  { key: 'critical', label: 'Critical', range: 'below 50%', color: '#E11D48' },
  { key: 'noData', label: 'No marks yet', range: '—', color: '#94A3B8' },
];

export const bandColor = (value) => {
  if (value === null || value === undefined) return RT.faint;
  if (value >= 90) return BANDS[0].color;
  if (value >= 75) return BANDS[1].color;
  if (value >= 50) return BANDS[2].color;
  return BANDS[3].color;
};

export const fmtPct = (value) => (value === null || value === undefined ? '—' : `${Number(value).toFixed(Number(value) % 1 ? 1 : 0)}%`);
export const fmtNum = (value) => Number(value || 0).toLocaleString('en-IN');
export const fmtDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—');
export const fmtShortDate = (value) => (value ? dayjs(value).format('DD MMM') : '—');
export const fmtTime = (value) => (value ? dayjs(`1970-01-01T${value}`).format('hh:mm A') : '');
export const fmtMonth = (value) => (value ? dayjs(`${value}-01`).format('MMM YY') : '');

export const STATUS_META = {
  present: { label: 'Present', color: '#16A34A', icon: 'solar:check-circle-bold' },
  absent: { label: 'Absent', color: '#E11D48', icon: 'solar:close-circle-bold' },
  na: { label: 'Not applicable', color: '#94A3B8', icon: 'solar:minus-circle-bold' },
  not_marked: { label: 'Not marked', color: '#F59E0B', icon: 'solar:question-circle-bold' },
};

export const numberSx = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

// Big gradient KPI card with soft decorative circles
export const GradientStatCard = ({ gradient = GRADIENTS.indigo, icon, label, value, sub, footer, loading, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      position: 'relative',
      overflow: 'hidden',
      border: 'none',
      color: '#fff',
      background: gradient,
      minHeight: 132,
      cursor: onClick ? 'pointer' : 'default',
      boxShadow: '0 14px 30px -18px rgba(30,27,75,.65)',
      transition: 'transform .2s ease, box-shadow .2s ease',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 22px 40px -20px rgba(30,27,75,.75)' },
    }}
  >
    <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', top: -50, right: -40, bgcolor: 'rgba(255,255,255,.14)' }} />
    <Box sx={{ position: 'absolute', width: 90, height: 90, borderRadius: '50%', bottom: -40, right: 40, bgcolor: 'rgba(255,255,255,.08)' }} />
    <Stack sx={{ position: 'relative', p: 2.25, height: '100%' }} gap={1}>
      <Stack direction="row" alignItems="center" gap={1.25}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(255,255,255,.2)',
            backdropFilter: 'blur(4px)',
            flexShrink: 0,
          }}
        >
          <Icon icon={icon} width={22} height={22} />
        </Box>
        <Typography sx={{ fontFamily: BODY_FONT, fontSize: 13, fontWeight: 600, opacity: 0.92, lineHeight: 1.25 }}>{label}</Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="text" width="55%" sx={{ fontSize: 34, bgcolor: 'rgba(255,255,255,.25)' }} />
      ) : (
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: 32, fontWeight: 700, lineHeight: 1.1, ...numberSx }}>
          {value ?? '—'}
        </Typography>
      )}
      {sub && <Typography sx={{ fontFamily: BODY_FONT, fontSize: 12.5, opacity: 0.88 }}>{sub}</Typography>}
      {footer}
    </Stack>
  </Card>
);

// Thin white progress bar for use inside gradient cards
export const GlassBar = ({ value }) => (
  <Box sx={{ height: 6, borderRadius: 99, bgcolor: 'rgba(255,255,255,.25)', overflow: 'hidden', mt: 0.5 }}>
    <Box sx={{ width: `${Math.min(Number(value) || 0, 100)}%`, height: '100%', borderRadius: 99, bgcolor: '#fff' }} />
  </Box>
);

// Circular attendance meter with the % in the middle
export const AttendanceRing = ({ value, size = 76, thickness = 5, label, color }) => {
  const ringColor = color || bandColor(value);
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <CircularProgress variant="determinate" value={100} size={size} thickness={thickness} sx={{ color: alpha(ringColor, 0.15), position: 'absolute' }} />
      <CircularProgress
        variant="determinate"
        value={Math.min(Number(value) || 0, 100)}
        size={size}
        thickness={thickness}
        sx={{ color: ringColor, position: 'absolute', '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }}
      />
      <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: size * 0.22, lineHeight: 1, color: RT.ink, ...numberSx }}>
          {fmtPct(value)}
        </Typography>
        {label && <Typography sx={{ fontSize: Math.max(size * 0.12, 9.5), color: RT.muted, fontWeight: 600, mt: 0.25 }}>{label}</Typography>}
      </Stack>
    </Box>
  );
};

// Horizontal % bar, coloured by attendance band unless a colour is passed
export const PercentBar = ({ value, color, height = 8, track = '#EEF0F6' }) => (
  <Box sx={{ height, borderRadius: 99, bgcolor: track, overflow: 'hidden', flex: 1, minWidth: 40 }}>
    <Box
      sx={{
        width: `${Math.min(Number(value) || 0, 100)}%`,
        height: '100%',
        borderRadius: 99,
        background: `linear-gradient(90deg, ${alpha(color || bandColor(value), 0.7)}, ${color || bandColor(value)})`,
        transition: 'width .6s ease',
      }}
    />
  </Box>
);

// "87.5%" pill tinted by band
export const PctPill = ({ value, size = 'medium' }) => {
  const color = bandColor(value);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: size === 'small' ? 0.75 : 1,
        py: size === 'small' ? 0.1 : 0.25,
        borderRadius: 99,
        fontSize: size === 'small' ? 11.5 : 12.5,
        fontWeight: 800,
        color,
        bgcolor: alpha(color, 0.12),
        ...numberSx,
      }}
    >
      {fmtPct(value)}
    </Box>
  );
};

// Last few marked sessions as coloured dots (oldest → newest)
export const RecentDots = ({ recent = [] }) =>
  recent.length ? (
    <Stack direction="row" gap={0.5} alignItems="center">
      {recent.map((item, index) => (
        <Tooltip key={`${item.date}-${index}`} title={`${fmtDate(item.date)} · ${item.subject || ''} · ${STATUS_META[item.status]?.label || item.status}`} arrow>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_META[item.status]?.color || RT.faint, flexShrink: 0 }} />
        </Tooltip>
      ))}
    </Stack>
  ) : (
    <Typography variant="caption" sx={{ color: RT.faint }}>
      —
    </Typography>
  );

// White card with a gradient-text title
export const Panel = ({ icon, title, subtitle, action, children, color = RT.primary, sx, bodySx }) => (
  <Card sx={{ borderRadius: 4, ...sx }}>
    {(title || action) && (
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={1.5} sx={{ px: 2.5, pt: 2.25, pb: 0.5 }}>
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ flex: 1, minWidth: 0 }}>
          {icon && (
            <Box sx={{ width: 34, height: 34, borderRadius: 2.5, display: 'grid', placeItems: 'center', color, bgcolor: alpha(color, 0.12), flexShrink: 0 }}>
              <Icon icon={icon} width={19} height={19} />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: 18, fontWeight: 700, color: RT.ink, lineHeight: 1.25 }} noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: RT.muted }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {action}
      </Stack>
    )}
    <Box sx={{ p: 2.5, pt: 1.75, ...bodySx }}>{children}</Box>
  </Card>
);

export const BandLegend = () => (
  <Stack direction="row" gap={1.75} flexWrap="wrap">
    {BANDS.slice(0, 4).map((band) => (
      <Stack key={band.key} direction="row" alignItems="center" gap={0.6}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: band.color }} />
        <Typography variant="caption" sx={{ color: RT.ink2, fontWeight: 600 }}>
          {band.label} <span style={{ color: RT.faint }}>{band.range}</span>
        </Typography>
      </Stack>
    ))}
  </Stack>
);

export const EmptyBlock = ({ icon = 'solar:calendar-search-bold-duotone', title = 'Nothing to show yet', caption }) => (
  <Stack alignItems="center" justifyContent="center" gap={0.75} sx={{ py: 4, textAlign: 'center' }}>
    <Box component={Icon} icon={icon} width={42} height={42} sx={{ color: RT.faint }} />
    <Typography variant="subtitle2" sx={{ color: RT.ink2 }}>
      {title}
    </Typography>
    {caption && (
      <Typography variant="caption" sx={{ color: RT.faint }}>
        {caption}
      </Typography>
    )}
  </Stack>
);

export const LoadingBlock = ({ rows = 4 }) => (
  <Stack gap={1}>
    {Array.from({ length: rows }).map((_, index) => (
      <Skeleton key={index} variant="rounded" height={38} />
    ))}
  </Stack>
);

// Small label/value stat used in headers and cards
export const MiniStat = ({ label, value, color = RT.ink, align = 'left' }) => (
  <Box sx={{ textAlign: align, minWidth: 0 }}>
    <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: 18, fontWeight: 700, color, lineHeight: 1.2, ...numberSx }}>{value ?? '—'}</Typography>
    <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 600 }} noWrap component="div">
      {label}
    </Typography>
  </Box>
);

// Small footnote when some saved marks could not be counted (so totals never silently disagree with the calendar)
export const DataCheckNote = ({ check }) => {
  if (!check) return null;
  const notes = [
    check.marksWithoutSession && `${fmtNum(check.marksWithoutSession)} marks are not counted: their session was removed or rescheduled`,
    check.marksOfUnknownStudents && `${fmtNum(check.marksOfUnknownStudents)} marks are not counted: the student was never assigned to this batch`,
    check.duplicateMarks && `${fmtNum(check.duplicateMarks)} duplicate marks were ignored (latest one kept)`,
    check.unknownRemarks && `${fmtNum(check.unknownRemarks)} marks have an unknown status and are counted as N/A`,
    check.wiseAutoAbsent && `${fmtNum(check.wiseAutoAbsent)} absences come from Wise sessions the student did not join`,
  ].filter(Boolean);
  if (!notes.length) return null;
  return (
    <Stack direction="row" gap={1} alignItems="flex-start" sx={{ color: RT.muted, px: 0.5 }}>
      <Icon icon="solar:info-circle-linear" width={16} style={{ marginTop: 2, flexShrink: 0 }} />
      <Typography variant="caption">{notes.join(' · ')}.</Typography>
    </Stack>
  );
};
